import { useState } from 'react'

import { needsUpgrade } from '@/shared/api'
import { useRunPipeline } from '@/shared/api/queries'
import type { SurveyPipeline, SurveyPipelineResult } from '@/shared/api/queries'
import { joinSides } from '@/shared/image'
import { getFile, useKeepLocally } from '@/shared/vault'

export interface Reading {
  read: (pipeline: SurveyPipeline) => void
  pending: boolean
  error: Error | null
  lost: boolean
  result: SurveyPipelineResult | undefined
}

export function useReading(tag: string, onQuota: () => void): Reading {
  const run = useRunPipeline()
  const { keep } = useKeepLocally()
  const [preparing, setPreparing] = useState(false)
  const [lost, setLost] = useState(false)
  const [result, setResult] = useState<SurveyPipelineResult>()

  async function prepare(pipeline: SurveyPipeline): Promise<File | undefined> {
    const kept = await Promise.all(pipeline.inputs.map((input) => getFile(input.path)))

    const files = kept.flatMap((blob, index) =>
      blob
        ? [blob instanceof File ? blob : new File([blob], `${index}.jpg`, { type: blob.type })]
        : [],
    )

    return files.length > 0 ? joinSides(files) : undefined
  }

  function read(pipeline: SurveyPipeline) {
    setLost(false)
    setPreparing(true)
    run.reset()

    void prepare(pipeline).then((file) => {
      setPreparing(false)

      if (!file) {
        setLost(true)

        return
      }

      run.mutate(
        { tag, pipeline: pipeline.tag, file },
        {
          onSuccess: (done) => {
            const documentId = done.outputs?.document_id

            if (documentId) {
              keep({
                documentId,
                file,
                fileName: file.name,
                ...(done.revealed?.document_number
                  ? { number: done.revealed.document_number }
                  : {}),
              })
            }

            setResult(done)
          },
          onError: (error) => {
            if (needsUpgrade(error)) {
              onQuota()
            }
          },
        },
      )
    })
  }

  return { read, pending: preparing || run.isPending, error: run.error, lost, result }
}
